import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

interface CommandStatus {
  id: string;
  type: string;
  reason: string | null;
  status: 'queued' | 'delivered';
  issuedAt: string;
  deliveredAt: string | null;
}

interface Props {
  employeeId: number;
  employeeName: string;
  /** Drives which actions make sense; `none` means no shift is open. */
  sessionStatus: 'active' | 'stale' | 'ended' | 'force_closed' | 'none';
}

/**
 * Remote control for one employee's tracking.
 *
 * The hard part here is honesty, not the buttons. There is no push channel, so
 * nothing takes effect the instant it is clicked — a command is queued and
 * collected on the device's next check-in (~60s while tracking, ~2 min
 * otherwise, and not at all until the phone has signal). A control that looked
 * instant would leave an admin believing a tracker had stopped when it had not,
 * which is the same silent-failure class the rest of this system works to
 * eliminate.
 *
 * So every action reports its real state — `queued` until a device has taken
 * it, `delivered` once one has — and the copy says "on next check-in" rather
 * than implying immediacy.
 */
export function TrackingControls({ employeeId, employeeName, sessionStatus }: Props) {
  const queryClient = useQueryClient();
  const tracking = sessionStatus === 'active' || sessionStatus === 'stale';

  const commands = useQuery({
    queryKey: ['commands', employeeId],
    queryFn: () => api.get<CommandStatus[]>(`/admin/employees/${employeeId}/commands`),
    // Fast enough to show a queued command flipping to delivered while the
    // admin is still looking at it.
    refetchInterval: 5_000,
  });

  const issue = useMutation({
    mutationFn: (type: string) => api.post(`/admin/employees/${employeeId}/commands`, { type }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commands', employeeId] }),
  });

  const pending = commands.data?.data ?? [];
  const pendingOf = (type: string) => pending.find((c) => c.type === type);

  return (
    <div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            tracking ? 'bg-success' : 'bg-muted-foreground',
          )}
        />
        <span className="text-sm font-medium">
          {tracking ? 'Recording' : 'Standing by'}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {tracking
          ? `Recording ${employeeName}'s location.`
          : `Not recording. ${employeeName}'s app is standing by and will act on the next check-in.`}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <CommandButton
          label="Start tracking"
          type="start_tracking"
          disabled={tracking || issue.isPending}
          pending={pendingOf('start_tracking')}
          onClick={() => issue.mutate('start_tracking')}
        />
        <CommandButton
          label="Stop tracking"
          type="stop_tracking"
          variant="danger"
          disabled={!tracking || issue.isPending}
          pending={pendingOf('stop_tracking')}
          onClick={() => issue.mutate('stop_tracking')}
        />
        <CommandButton
          label="Locate now"
          type="locate_now"
          // Works whether or not tracking is running: an idle device opens a
          // session, takes one fix and closes it again.
          disabled={issue.isPending}
          pending={pendingOf('locate_now')}
          onClick={() => issue.mutate('locate_now')}
        />
        <CommandButton
          label="Get call logs"
          type="sync_call_logs"
          // On-demand call-activity pull. Does nothing on the device unless the
          // employee has consented to call sharing; the panel still shows it
          // queued/delivered so the admin can see it was received.
          disabled={issue.isPending}
          pending={pendingOf('sync_call_logs')}
          onClick={() => issue.mutate('sync_call_logs')}
        />
      </div>

      <MicControls
        issue={issue}
        pendingStart={pendingOf('start_mic_capture')}
        pendingStop={pendingOf('stop_mic_capture')}
      />

      {issue.error instanceof ApiError && (
        <p className="mt-2 text-xs text-danger">{issue.error.message}</p>
      )}

      {pending.length > 0 && (
        <ul className="mt-2 space-y-1">
          {pending.map((c) => (
            <li key={c.id} className="text-xs text-muted-foreground">
              <span className="font-medium">{LABELS[c.type] ?? c.type}</span>
              {c.status === 'queued' ? (
                <> · waiting for the device to check in</>
              ) : (
                <> · sent to the device, awaiting confirmation</>
              )}
            </li>
          ))}
        </ul>
      )}

      <PinControl employeeId={employeeId} />

      <p className="mt-2 text-xs text-muted-foreground">
        Commands reach the device on its next check-in — about a minute while recording, up to two
        when standing by. Nothing is instant, and this panel says{' '}
        <span className="font-medium">queued</span> until a device has actually taken the
        instruction.
      </p>
    </div>
  );
}

const LABELS: Record<string, string> = {
  start_tracking: 'Start tracking',
  stop_tracking: 'Stop tracking',
  locate_now: 'Locate now',
  sync_call_logs: 'Get call logs',
  force_sync: 'Force sync',
  start_mic_capture: 'Start voice recording',
  stop_mic_capture: 'Stop voice recording',
};

/**
 * Voice recording, set apart from the other controls.
 *
 * Deliberately its own block with its own explanation, because it is the most
 * sensitive control on the page and the copy has to be honest about two things
 * a supervisor might otherwise assume wrongly: it is not instant (it rides the
 * same check-in as everything else), and it is not silent (the device records
 * only with the employee's standing consent, and Android shows a microphone
 * indicator the whole time). A control that implied either would be lying about
 * what the system does.
 */
function MicControls({
  issue,
  pendingStart,
  pendingStop,
}: {
  issue: ReturnType<typeof useMutation<unknown, Error, string>>;
  pendingStart?: CommandStatus | undefined;
  pendingStop?: CommandStatus | undefined;
}) {
  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-muted-foreground">
          <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
          <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4" />
        </svg>
        <span className="text-sm font-medium">Voice recording</span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <CommandButton
          label="Start recording"
          type="start_mic_capture"
          disabled={issue.isPending}
          pending={pendingStart}
          onClick={() => issue.mutate('start_mic_capture')}
        />
        <CommandButton
          label="Stop recording"
          type="stop_mic_capture"
          variant="danger"
          disabled={issue.isPending}
          pending={pendingStop}
          onClick={() => issue.mutate('stop_mic_capture')}
        />
      </div>

      <p className="mt-2 text-2xs text-muted-foreground">
        Records the device microphone in one-minute segments while switched on, and uploads them to
        the Voice tab. It does nothing unless the employee has consented on their device and granted
        the microphone permission, and Android shows its recording indicator the whole time — this
        cannot capture silently. Like every command here, it takes effect on the device&rsquo;s next
        check-in, not instantly.
      </p>
    </div>
  );
}

function CommandButton({
  label,
  disabled,
  pending,
  variant,
  onClick,
}: {
  label: string;
  type: string;
  disabled: boolean;
  pending?: CommandStatus | undefined;
  variant?: 'danger';
  onClick: () => void;
}) {
  const queued = Boolean(pending);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || queued}
      className={cn(
        'rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'danger'
          ? 'border-danger/40 text-danger hover:bg-danger/10'
          : 'border-border hover:bg-muted',
      )}
    >
      {queued ? `${label} — queued` : label}
    </button>
  );
}

/**
 * Sets the employee's app-unlock PIN.
 *
 * The PIN is never read back — not into this form, not into a log. An admin can
 * set a new one; nobody can ask the system what the current one is, which is
 * the only arrangement under which "the admin changed it" and "the admin knew
 * it" stay separable.
 */
function PinControl({ employeeId }: { employeeId: number }) {
  const [pin, setPin] = useState('');
  const [open, setOpen] = useState(false);

  const save = useMutation({
    mutationFn: (value: string) => api.put(`/admin/employees/${employeeId}/pin`, { pin: value }),
    onSuccess: () => setPin(''),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted"
      >
        Change app PIN
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        New app PIN <span className="font-normal">(4–8 digits)</span>
      </label>
      <div className="flex gap-2">
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          inputMode="numeric"
          placeholder="7406"
          className="w-24 rounded-md border border-border bg-background px-2 py-1 text-sm"
        />
        <button
          type="button"
          disabled={pin.length < 4 || save.isPending}
          onClick={() => save.mutate(pin)}
          className="rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setPin('');
            save.reset();
          }}
          className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
        >
          Cancel
        </button>
      </div>
      {save.error instanceof ApiError && (
        <p className="text-xs text-danger">
          {save.error.fieldErrors?.pin?.[0] ?? save.error.message}
        </p>
      )}
      {save.isSuccess && (
        <p className="text-xs text-muted-foreground">
          Saved. The device applies it on its next check-in — until then the old PIN still works.
        </p>
      )}
    </div>
  );
}
